import { useEffect, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { InputField } from '@/components/InputField';
import {
  useFundraisingCampaignJustifyExpenses,
  usePutFundraisingCampaignJustifyExpensesMutation,
} from '@/services/queries/fundraising-campaign';
import { formatCurrency } from '@/utils';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  useToast,
} from '@chakra-ui/react';
import { expenseDetailSchema, modalFormSchema, type ModalFormData } from './schemas';
import { ReceiptUpload } from '../ReceiptUpload';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

export interface FundraisingCampaignJustifyExpensesModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  campaignId: string;
  onDone(): void;
}

export const FundraisingCampaignJustifyExpensesModal = ({
  campaignId,
  isOpen,
  onClose,
  onDone,
}: FundraisingCampaignJustifyExpensesModalProps) => {
  const { t } = useTranslation();
  const { data: itemData, isLoading, remove: removeQuery } = useFundraisingCampaignJustifyExpenses(campaignId, isOpen);
  const { mutate: saveItem, isLoading: isLoadingSaveItem } = usePutFundraisingCampaignJustifyExpensesMutation();
  const toast = useToast();

  const formRef = useRef<HTMLFormElement>(null);
  const {
    register,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control,
    watch,
    handleSubmit,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      thank_you_message: '',
      total_expenses: 0,
      expenses_details: [],
    },
    resolver: zodResolver(modalFormSchema),
  });
  const { fields, update, append, remove } = useFieldArray({
    control,
    name: 'expenses_details',
  });

  const formValues = watch();

  const handleSave = () => {
    formRef?.current?.requestSubmit();
  };

  const onSubmit = (data: ModalFormData) => {
    saveItem(
      {
        id: campaignId,
        payload: {
          thank_you_message: data.thank_you_message,
          total_expenses: data.total_expenses ?? 0,
          expenses_details: (data.expenses_details ?? [])?.map(item => ({
            title: item.title,
            value: item.value,
            receipt: item.receipt as File,
          })),
        },
      },
      {
        onSuccess: () => {
          toast({
            title: t(
              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_success_toast_title',
            ),
            status: 'success',
          });
          onDone?.();
        },
        onError: () => {
          toast({
            title: t(
              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_error_toast_title',
            ),
            status: 'error',
          });
          onDone?.();
        },
      },
    );
  };

  const hasEditingValues = Boolean(formValues?.expenses_details?.some(detail => detail.is_editing));

  const handleClose = () => {
    onClose?.();
    reset();
    removeQuery();
  };

  const handleSaveEditingItem = (itemIndex: number) => {
    const valid = expenseDetailSchema.safeParse(formValues?.expenses_details?.[itemIndex]);

    if (!valid?.success) {
      valid.error.issues.forEach(error => {
        const [field] = error.path;

        setError(`expenses_details.${itemIndex}.${field as 'title' | 'value' | 'receipt'}`, {
          message: error.message,
        });
      });

      return;
    }

    update(itemIndex, {
      ...(formValues?.expenses_details?.[itemIndex] as any),
      is_editing: false,
    });
    clearErrors(`expenses_details.${itemIndex}`);
  };

  const handleEditItem = (itemIndex: number) => {
    update(itemIndex, {
      ...(formValues?.expenses_details?.[itemIndex] as any),
      is_editing: true,
    });
  };

  useEffect(() => {
    if (!itemData) return;

    if (itemData?.thank_you_message) setValue('thank_you_message', itemData?.thank_you_message);
    if (itemData?.total_expenses) setValue('total_expenses', itemData?.total_expenses / 100);
    if (itemData?.expenses_details) {
      itemData?.expenses_details?.map((item, index) => {
        if (item?.receipt_url) {
          fetch(item?.receipt_url)
            .then(res => res.blob())
            .then(blob => {
              const imageName = item?.receipt_url?.split('/')?.pop() ?? 'comprovante';

              const backendFile = new File([blob], imageName, {
                type: blob.type,
              });

              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(backendFile);
              setValue(`expenses_details.${index}`, {
                title: itemData?.expenses_details?.[index]?.title,
                value: itemData?.expenses_details?.[index]?.value / 100,
                receipt: dataTransfer.files[0],
                is_editing: false,
              });

              update(index, {
                title: itemData?.expenses_details?.[index]?.title,
                value: itemData?.expenses_details?.[index]?.value / 100,
                receipt: dataTransfer.files[0],
                is_editing: false,
              });
            })
            .catch(err => console.log(err));
        }
      });
    }
  }, [itemData, setValue, update]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent minW={{ md: '500px' }}>
        <ModalHeader>
          {t(
            'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_modal_title',
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction='column' gap={4} as='form' onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <Flex gap={5} direction={{ base: 'column', md: 'row' }}>
              <Flex direction='column' maxW='50%'>
                <Text fontSize='14px' fontWeight='medium'>
                  {t(
                    'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_campaign_label',
                  )}
                </Text>
                <Text>{itemData?.campaign_name}</Text>
              </Flex>

              <Flex direction='column'>
                <Text fontSize='14px' fontWeight='medium'>
                  {t(
                    'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_collected_value_label',
                  )}
                </Text>
                <Text>
                  {itemData?.collected_value
                    ? formatCurrency(itemData?.collected_value)
                    : t(
                        'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_collected_value_empty',
                      )}
                </Text>
              </Flex>
            </Flex>

            <InputField
              withoutFloating
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_thank_you_message_label',
              )}
              {...register('thank_you_message')}
              helperText={t(
                'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_thank_you_message_helper_text',
              )}
              maxLength={4000}
              rootProps={{ isInvalid: !!errors?.thank_you_message, errorMessage: errors?.thank_you_message?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_total_expenses_label',
              )}
              type='number'
              {...register('total_expenses', { valueAsNumber: true })}
              helperText={t(
                'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_total_expenses_helper_text',
              )}
              maxLength={4000}
              rootProps={{ isInvalid: !!errors?.total_expenses, errorMessage: errors?.total_expenses?.message }}
            />

            <Flex direction='column' gap={1}>
              <Text fontSize='14px' fontWeight='medium'>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_details_label',
                )}
              </Text>

              <Flex direction='column' gap={{ base: 8, md: 4 }}>
                {fields?.map((field, index) => {
                  if (field.is_editing) {
                    return (
                      <Flex
                        key={index}
                        direction={{ base: 'column', md: 'row' }}
                        justifyContent='space-between'
                        gap={{ base: 2, md: 4 }}
                        w='100%'
                      >
                        <Flex gap={4} w='100%'>
                          <InputField
                            withoutFloating
                            label={t(
                              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_item_label',
                            )}
                            {...register(`expenses_details.${index}.title`)}
                            helperText={t(
                              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_item_helper_text',
                            )}
                            maxLength={30}
                            rootProps={{
                              w: '130px',
                              isInvalid: !!errors?.expenses_details?.[index]?.title,
                              errorMessage: errors?.expenses_details?.[index]?.title?.message,
                            }}
                          />

                          <InputField
                            withoutFloating
                            type='number'
                            label={t(
                              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_value_label',
                            )}
                            {...register(`expenses_details.${index}.value`, {
                              setValueAs: value => value * 100,
                            })}
                            maxLength={30}
                            rootProps={{
                              w: '100px',
                              isInvalid: !!errors?.expenses_details?.[index]?.value,
                              errorMessage: errors?.expenses_details?.[index]?.value?.message,
                            }}
                          />

                          <Box w='120px'>
                            <ReceiptUpload
                              name={`expenses_details.${index}.receipt`}
                              control={control}
                              error={errors?.expenses_details?.[index]?.receipt?.message?.toString()}
                              wrapperProps={{ minH: '61px', alignItems: 'flex-end' }}
                            />
                          </Box>
                        </Flex>

                        <Button
                          type='button'
                          alignSelf={{ base: 'flex-start', md: 'center' }}
                          h='28px'
                          w='fit-content'
                          onClick={() => handleSaveEditingItem(index)}
                        >
                          {t(
                            'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_save_button',
                          )}
                        </Button>
                      </Flex>
                    );
                  }

                  return (
                    <Flex
                      key={field.title}
                      direction={{ base: 'column', md: 'row' }}
                      justifyContent='space-between'
                      gap={{ base: 2, md: 4 }}
                    >
                      <Flex gap={4} w='100%'>
                        <Text w='130px' noOfLines={1} title={field.title}>
                          {field.title}
                        </Text>
                        <Text w='100px' noOfLines={1}>
                          {field.value
                            ? formatCurrency(field.value)
                            : t(
                                'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_collected_value_empty',
                              )}
                        </Text>
                        <Text w='120px' noOfLines={1} textDecoration='underline'>
                          {(field.receipt as File).name}
                        </Text>
                      </Flex>

                      <Flex gap={4}>
                        <ChakraFeatherIcon
                          w='20px'
                          cursor='pointer'
                          aria-label='Editar'
                          name='edit-2'
                          color='brand.500'
                          onClick={() => handleEditItem(index)}
                        />
                        <ChakraFeatherIcon
                          w='20px'
                          cursor='pointer'
                          aria-label='Deletar'
                          name='trash-2'
                          color='red.500'
                          onClick={() => remove(index)}
                        />
                      </Flex>
                    </Flex>
                  );
                })}

                <Button
                  alignSelf='flex-start'
                  variant='link'
                  color='brand.500'
                  textDecor='underline'
                  display='flex'
                  alignItems='center'
                  gap={2}
                  isDisabled={hasEditingValues}
                  onClick={() => append({ title: '', value: 0, receipt: '', is_editing: true })}
                >
                  <ChakraFeatherIcon name='plus' />
                  {t(
                    'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_add_expense_button',
                  )}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter gap='kilo'>
          <Button variant='outlined' color='gray.600' onClick={handleClose}>
            {t(
              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_close_button',
            )}
          </Button>
          <Button onClick={handleSave} isLoading={isLoadingSaveItem}>
            {t(
              'pages.fundraising_campaign.components.fundraising_campaign_justify_expenses_modal.fundraising_campaign_justify_expenses_save_button',
            )}
          </Button>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={isLoading || isLoadingSaveItem} />
    </Modal>
  );
};
